// src/components/canvass/DispositionModal.tsx
// Modal for updating address disposition/status during canvassing
import React from 'react';
import {
  X, Check, Ban, RefreshCw, UserX, DoorClosed, FileText,
  User, Phone, Mail, Plus, Edit2, Sparkles
} from 'lucide-react';
import { WorkloadAddress, AddressStatus } from '../../types/address.types';
import { Survey } from '../../types/survey.types';

// Disposition options with icons and styling
const DISPOSITIONS: Array<{
  status: AddressStatus;
  label: string;
  icon: React.FC<{ className?: string }>;
  color: string;
  description: string;
}> = [
  { status: 'completed', label: 'Completed', icon: Check, color: 'bg-green-500', description: 'Successfully spoke with resident' },
  { status: 'not_home', label: 'Not Home', icon: DoorClosed, color: 'bg-yellow-500', description: 'No one answered' },
  { status: 'refused', label: 'Refused', icon: UserX, color: 'bg-red-500', description: 'Resident declined to participate' },
  { status: 'inaccessible', label: 'Inaccessible', icon: Ban, color: 'bg-purple-500', description: 'Could not access property' },
  { status: 'visited', label: 'Needs Follow-up', icon: RefreshCw, color: 'bg-blue-500', description: 'Mark for return visit' },
];

export interface DispositionModalProps {
  selectedAddress: WorkloadAddress;
  quickResidentName: string;
  onResidentNameChange: (name: string) => void;
  onDisposition: (status: AddressStatus) => void;
  onClose: () => void;
  onAddUnit: () => void;
  onEditTargetInfo: () => void;
  isSubmitting: boolean;
  survey: Survey | null;
  isLeadProject: boolean;
}

const DispositionModal: React.FC<DispositionModalProps> = ({
  selectedAddress,
  quickResidentName,
  onResidentNameChange,
  onDisposition,
  onClose,
  onAddUnit,
  onEditTargetInfo,
  isSubmitting,
  survey,
  isLeadProject,
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 md:items-center">
      <div className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Update Status</h2>
            <p className="text-sm text-gray-500">
              {selectedAddress.street}
              {selectedAddress.unit && ` Unit ${selectedAddress.unit}`}
            </p>
          </div>
          <button onClick={onClose} className="p-2">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Quick Resident Name Input */}
        <div className="px-4 pt-3 pb-3 bg-indigo-50 border-b">
          <label className="block text-xs font-medium text-indigo-700 uppercase tracking-wide mb-1.5">
            Resident Name
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-indigo-400" />
            <input
              type="text"
              placeholder="Enter resident's name"
              value={quickResidentName}
              onChange={(e) => onResidentNameChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-indigo-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
              autoComplete="off"
            />
          </div>
        </div>

        {/* Additional Info Section */}
        <div className="px-4 pt-2 pb-2 bg-gray-50 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              {selectedAddress.residentPhone && (
                <span className="flex items-center">
                  <Phone className="h-3.5 w-3.5 mr-1 text-gray-400" />
                  {selectedAddress.residentPhone}
                </span>
              )}
              {selectedAddress.residentEmail && (
                <span className="flex items-center">
                  <Mail className="h-3.5 w-3.5 mr-1 text-gray-400" />
                  {selectedAddress.residentEmail}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onAddUnit}
                className="text-xs text-green-600 hover:text-green-700 flex items-center"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Unit
              </button>
              <button
                onClick={onEditTargetInfo}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center"
              >
                <Edit2 className="h-3 w-3 mr-1" />
                More Info
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {DISPOSITIONS.map(({ status, label, icon: Icon, color, description }) => (
            <button
              key={status}
              onClick={() => onDisposition(status)}
              disabled={isSubmitting}
              className={`w-full flex items-center p-3 rounded-lg border-2 transition-all ${
                selectedAddress.status === status
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              } ${isSubmitting ? 'opacity-50' : ''}`}
            >
              <div className={`w-10 h-10 ${color} rounded-full flex items-center justify-center text-white mr-3`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-900">{label}</p>
                <p className="text-sm text-gray-500">{description}</p>
              </div>
            </button>
          ))}
        </div>

        {survey && (
          <div className="p-4 border-t bg-gray-50">
            <p className="text-sm text-gray-600 flex items-center">
              <FileText className="h-4 w-4 mr-2" />
              Survey: {survey.name} will open after "Completed"
            </p>
          </div>
        )}

        {/* Lead generation mode hint */}
        {isLeadProject && !survey && (
          <div className="p-4 border-t bg-emerald-50">
            <p className="text-sm text-emerald-700 flex items-center">
              <Sparkles className="h-4 w-4 mr-2" />
              Lead capture form will open after "Completed"
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DispositionModal;
